import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { bookings } from '../../api';
import '../Homework/Homework.css';

// A player asked for a coaching slot: this pops up over whatever the coach is doing, with the request and two big
// buttons, Accept and Deny. It opens when the request notification arrives (app open) or is tapped (app closed or in
// the background). Several requests queue up and are answered one after the other.

const whenText = (b) => {
  const start = b.scheduledStartTime || b.slotId?.startTime;
  const end = b.scheduledEndTime || b.slotId?.endTime;
  if (!start) return '';
  const s = new Date(start);
  const day = s.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
  const time = (d) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time(s)}${end ? ` – ${time(new Date(end))}` : ''}`;
};

// Who is signed in (read when an event arrives, so it is right even if the popup was mounted before login)
const signedIn = () => {
  try { return JSON.parse(localStorage.getItem('user') || 'null') || {}; } catch (e) { return {}; }
};
const isCoachRole = (u) => u.role === 'coach' || u.role === 'headcoach';

const BookingRequestPopup = () => {
  const [queue, setQueue] = useState([]);     // booking ids waiting for an answer
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState('');    // '' | 'approved' | 'denied'
  const [error, setError] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [message, setMessage] = useState('');
  const current = queue[0];

  const add = useCallback((id) => {
    if (!id) return;
    setQueue(prev => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  // New request notification while the app is open (data comes from the push)
  useEffect(() => {
    const onNotif = (e) => {
      const d = (e && e.detail && e.detail.data) || {};
      if (!isCoachRole(signedIn())) return;
      if (d.type === 'booking_request' && d.bookingId && !d.headcoachCopy) add(d.bookingId);
    };
    window.addEventListener('inAppNotification', onNotif);
    return () => window.removeEventListener('inAppNotification', onNotif);
  }, [add]);

  // Tapped notification (cold start or background): the tap handler leaves the booking id for us
  useEffect(() => {
    const take = () => {
      let id = '';
      try { id = sessionStorage.getItem('focusBookingRequest') || ''; sessionStorage.removeItem('focusBookingRequest'); } catch (e) { /* ignore */ }
      if (id && isCoachRole(signedIn())) add(id);
    };
    take();
    window.addEventListener('focusBookingRequest', take);
    return () => window.removeEventListener('focusBookingRequest', take);
  }, [add]);

  // Load the request on top of the queue
  useEffect(() => {
    if (!current) { setBooking(null); return undefined; }
    let alive = true;
    setBooking(null); setResult(''); setError(''); setShowMessage(false); setMessage('');
    bookings.getBooking(current)
      .then(res => {
        if (!alive) return;
        const b = res.data.booking;
        // Not addressed to me (a head coach's copy), or already handled: nothing to decide here
        const me = signedIn();
        const mine = String(b.coachId?._id || b.coachId) === String(me._id || me.id);
        if (!mine) { setQueue(prev => prev.slice(1)); return; }
        setBooking(b);
      })
      .catch(() => { if (alive) setQueue(prev => prev.slice(1)); });
    return () => { alive = false; };
  }, [current]);

  const next = useCallback(() => setQueue(prev => prev.slice(1)), []);

  const respond = async (kind) => {
    if (busy || !booking) return;
    setBusy(kind);
    setError('');
    try {
      const text = message.trim() || null;
      if (kind === 'approved') await bookings.approveBooking(booking._id, text);
      else await bookings.denyBooking(booking._id, text, 'available');
      setResult(kind);
      window.dispatchEvent(new Event('bookingRequestAnswered')); // lists of requests can refresh
      setTimeout(next, 1400);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not send your answer. Try again.');
    } finally {
      setBusy('');
    }
  };

  if (!current || !booking) return null;

  const pending = booking.status === 'pending';
  const who = booking.requesterName || booking.requesterId?.name || 'A player';
  const slotTitle = booking.slotTitle || booking.slotId?.title || 'Coaching slot';
  const names = (booking.playerNames || []).filter(Boolean);

  return createPortal(
    <div className="bq-back" role="dialog" aria-modal="true" aria-label="Coaching request">
      <div className="bq-sheet">
        {result ? (
          <div className="bq-done" role="status">
            <div className="bq-done-icon">{result === 'approved' ? '✅' : '❌'}</div>
            <div className="bq-done-text">{result === 'approved' ? 'Accepted' : 'Denied'}</div>
            <div className="bq-done-sub">{who} has been notified</div>
          </div>
        ) : (
          <>
            <div className="bq-kicker">
              <span>Coaching request</span>
              {queue.length > 1 && <span className="bq-more">+{queue.length - 1} more</span>}
            </div>
            <h2 className="bq-who">{who}</h2>
            <div className="bq-slot">{slotTitle}</div>
            <div className="bq-when">{whenText(booking)}</div>
            {booking.partySize > 1 && (
              <div className="bq-party">👥 {booking.partySize} players{names.length ? `: ${names.join(', ')}` : ''}</div>
            )}
            {booking.message && <div className="bq-msg">“{booking.message}”</div>}

            {pending ? (
              <>
                {showMessage && (
                  <textarea
                    className="bq-text"
                    rows={2}
                    maxLength={500}
                    placeholder="Message for the player (optional)"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                )}
                <div className="bq-actions">
                  <button type="button" className="bq-btn bq-deny" disabled={!!busy} onClick={() => respond('denied')}>
                    {busy === 'denied' ? '…' : '✕ Deny'}
                  </button>
                  <button type="button" className="bq-btn bq-accept" disabled={!!busy} onClick={() => respond('approved')}>
                    {busy === 'approved' ? '…' : '✓ Accept'}
                  </button>
                </div>
                {!showMessage && <button type="button" className="bq-link" onClick={() => setShowMessage(true)}>Add a message</button>}
                {error && <div className="bq-error">{error}</div>}
                <button type="button" className="bq-link" onClick={next}>Decide later</button>
              </>
            ) : (
              <>
                <div className="bq-party" style={{ marginTop: 14 }}>This request was already {booking.status}.</div>
                <button type="button" className="bq-btn bq-accept" style={{ width: '100%', marginTop: 12 }} onClick={next}>OK</button>
              </>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BookingRequestPopup;
